// Sidebar shell — persistent left rail + a drop-up user menu at its
// foot (Settings · Terms & Conditions · Log out). The main column hosts
// the active section. Overview and API keys live in the rail, plus
// Billing for single users (org members bill through their org); Logs
// is intentionally not registered here.

import React from "react";
import { flushSync } from "react-dom";

import { getTheme, setTheme, type ThemeChoice } from "../design/theme";
import { ToastProvider, useToast } from "../design/Toast";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Brand } from "../components/Brand";
import { InstallDialog } from "../components/InstallDialog";
import { Keys } from "../sections/Keys";
import { MyUsage, USAGE_LAYERS } from "../sections/insights/MyUsage";
import { INVENTORY_LAYERS } from "../sections/insights/Assets";
import { COST_VIEWS } from "../sections/insights/costViews";
import { Billing } from "../sections/Billing";
import { Credits } from "../sections/Credits";
import { Settings } from "../sections/Settings";
import { Legal } from "../sections/Legal";
import { Loader } from "../components/Loader";
import { PageBoundary } from "../components/PageBoundary";
import { RangeProvider } from "../sections/insights/range";
import { Glossary } from "../sections/insights/Glossary";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { OrgLeaderboard } from "../sections/OrgLeaderboard";
import { NavIcon, type NavIconName } from "./NavIcons";
import { Icon, LayersNav, type Layer } from "../design";

// Lazy: the org panel pulls in recharts; members who never see it
// shouldn't download it.
const Organization = React.lazy(() =>
  import("../sections/org").then((m) => ({ default: m.Organization })),
);
import { canManageDevices, orgTabs, type OrgTab } from "../sections/org/tabs";
import {
  ACTIVITY_LAYERS,
  GOVERNANCE_LAYERS,
  ORG_SETTINGS_LAYERS,
  peopleLayers,
} from "../sections/org/views";
import { initialsOf } from "../lib/format";
import type { AuthHeaders } from "../lib/auth";
import type { Me, Org } from "../lib/types";
import { isAdminRole } from "../lib/roles";
import { clearCachedMe } from "../lib/meCache";
import { clearSignupIntent } from "../lib/intent";
import { landingUrl } from "../lib/urls";
import { getHashParam, setHashParam } from "../lib/hashState";
import { readNavMode, writeNavMode, type NavMode } from "./navMode";
import {
  Redirect,
  TOUR_EVENT,
  isUnknownOrgTab,
  type Route,
  type Section,
} from "./route";

export function isOrgAdmin(org: Org | null): boolean {
  return !!org && isAdminRole(org.role);
}

const NAV: Array<{ key: Section; label: string; icon: NavIconName }> = [
  { key: "overview", label: "My usage", icon: "usage" },
  { key: "keys", label: "My API keys", icon: "keys" },
];

// Org pages that keep their view in ?tab= rather than the path.
const TAB_PARAM = new Set<OrgTab>(["plans", "settings"]);

const ORG_ICON: Record<OrgTab, NavIconName> = {
  home: "home",
  visibility: "analytics",
  activity: "usage",
  assets: "assets",
  budgets: "governance",
  plans: "seats",
  models: "models",
  settings: "settings",
};

const USER_MENU: Array<{ key: Section; label: string; icon: NavIconName }> = [
  { key: "account", label: "Account", icon: "account" },
  { key: "terms", label: "Terms & Conditions", icon: "terms" },
];

// Landing-hosted reading, reachable without leaving helm first.
const RESOURCES: Array<{ key: string; label: string; path: string }> = [
  { key: "docs", label: "Docs", path: "/docs/api/" },
  { key: "blog", label: "Blog", path: "/blog" },
];

/** Says once why a link landed somewhere other than where it pointed. */
function Rerouted({
  why,
  children,
}: {
  why: string;
  children: React.ReactNode;
}) {
  const toast = useToast();
  React.useEffect(() => {
    toast(why);
  }, [toast, why]);
  return <>{children}</>;
}

function SectionRouter({
  section,
  me,
  org,
  authHeaders,
  refresh,
  orgTab,
  orgSub,
  onOrgTab,
}: {
  section: Section;
  me: Me;
  org: Org | null;
  authHeaders: AuthHeaders;
  refresh: () => Promise<void>;
  orgTab: OrgTab;
  orgSub?: string;
  onOrgTab: (t: OrgTab, sub?: string) => void;
}) {
  switch (section) {
    case "usage":
    case "overview":
      return (
        <MyUsage
          me={me}
          authHeaders={authHeaders}
          refresh={refresh}
          sub={orgSub}
          onSub={(t) => onOrgTab("home", t)}
        />
      );
    case "keys":
      return <Keys me={me} authHeaders={authHeaders} refresh={refresh} />;
    case "billing":
      if (org && isOrgAdmin(org))
        return <Redirect to="#organization/settings?tab=billing" />;
      if (org) {
        return (
          <Rerouted why="Billing is handled by your organization's admins.">
            <MyUsage me={me} authHeaders={authHeaders} refresh={refresh} />
          </Rerouted>
        );
      }
      // No plan to manage: every "billing" link lands on the purse instead.
      if (me.sub_credited) return <Credits authHeaders={authHeaders} />;
      return <Billing authHeaders={authHeaders} />;
    case "credits":
      if (org && isOrgAdmin(org))
        return <Redirect to="#organization/settings?tab=billing" />;
      if (org || !(me.api_key_enabled || me.sub_credited)) {
        return <MyUsage me={me} authHeaders={authHeaders} refresh={refresh} />;
      }
      return <Credits authHeaders={authHeaders} />;
    case "leaderboard":
      if (org) {
        return <OrgLeaderboard org={org} authHeaders={authHeaders} />;
      }
      return <MyUsage me={me} authHeaders={authHeaders} refresh={refresh} />;
    case "organization":
      if (org && isOrgAdmin(org) && isUnknownOrgTab(window.location.hash)) {
        return (
          <Rerouted why="That page doesn't exist, so here is Home.">
            <Redirect to="#organization" />
          </Rerouted>
        );
      }
      if (org && isOrgAdmin(org)) {
        return (
          <React.Suspense fallback={<Loader />}>
            <Organization
              org={org}
              authHeaders={authHeaders}
              tab={orgTab}
              sub={orgSub}
              onTabChange={onOrgTab}
            />
          </React.Suspense>
        );
      }
      return (
        <Rerouted why="Organization pages are for org admins, so here is your own usage.">
          <MyUsage me={me} authHeaders={authHeaders} refresh={refresh} />
        </Rerouted>
      );
    case "account":
      return (
        <Settings
          me={me}
          org={org}
          authHeaders={authHeaders}
          refresh={refresh}
        />
      );
    case "terms":
      return <Legal />;
    default:
      return <MyUsage me={me} authHeaders={authHeaders} refresh={refresh} />;
  }
}

function UserMenu({
  open,
  onClose,
  onNavigate,
  onTour,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (s: Section) => void;
  onTour?: () => void;
}) {
  const { signOut } = useClerk();
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!ref.current?.contains(t) && !t.closest?.(".user-chip")) onClose();
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} className="avmenu fade-in">
      {USER_MENU.map((it) => (
        <button
          key={it.key}
          className="avmenu-item"
          type="button"
          onClick={() => {
            onNavigate(it.key);
            onClose();
          }}
        >
          <span className="glyph">
            <NavIcon name={it.icon} />
          </span>
          <span>{it.label}</span>
        </button>
      ))}
      {onTour && (
        <button
          className="avmenu-item"
          type="button"
          onClick={() => {
            onTour();
            onClose();
          }}
        >
          <span className="glyph">
            <NavIcon name="help" />
          </span>
          <span>Take the tour</span>
        </button>
      )}
      <ThemeSwitch />
      <button
        className="avmenu-item danger"
        type="button"
        onClick={() => {
          clearCachedMe();
          clearSignupIntent();
          signOut({ redirectUrl: landingUrl() });
        }}
      >
        <span className="glyph">
          <NavIcon name="logout" />
        </span>
        <span>Log out</span>
      </button>
    </div>
  );
}

const THEMES: { value: ThemeChoice; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

function ThemeSwitch() {
  const [theme, setThemeState] = React.useState<ThemeChoice>(getTheme);
  return (
    <div
      className="avmenu-theme"
      role="radiogroup"
      aria-label="Theme"
      onClick={(e) => e.stopPropagation()}
    >
      <span>Theme</span>
      <span className="ds-seg sm">
        {THEMES.map((th) => (
          <button
            key={th.value}
            type="button"
            role="radio"
            aria-checked={theme === th.value}
            className={theme === th.value ? "on" : undefined}
            onClick={() => {
              setTheme(th.value);
              setThemeState(th.value);
            }}
          >
            {th.label}
          </button>
        ))}
      </span>
    </div>
  );
}

/** Runs a sidebar width change as a view transition, so the rail slides
 * instead of snapping; instant where unsupported or motion is reduced. */
function slide(update: () => void) {
  if (
    !document.startViewTransition ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
    return update();
  const root = document.documentElement;
  root.classList.add("nav-sliding");
  void document
    .startViewTransition(() => flushSync(update))
    .finished.finally(() => root.classList.remove("nav-sliding"));
}

export function Sidebar({
  route: { section, orgTab, sub },
  navigate,
  me,
  org,
  authHeaders,
  refresh,
}: {
  route: Route;
  navigate: (s: Section, t?: OrgTab, sub?: string) => void;
  me: Me;
  org: Org | null;
  authHeaders: AuthHeaders;
  refresh: () => Promise<void>;
}) {
  const { user } = useUser();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const [resourcesOpen, setResourcesOpen] = React.useState(false);
  const [navMode, setNavModeState] = React.useState<NavMode>(readNavMode);
  const setNavMode = React.useCallback((m: NavMode) => {
    slide(() => setNavModeState(m));
    writeNavMode(m);
  }, []);
  const aside = React.useRef<HTMLElement>(null);
  const [flyKey, setFlyKey] = React.useState<string | null>(null);
  const [flyLeft, setFlyLeft] = React.useState(0);
  const flyRef = React.useRef<HTMLDivElement>(null);
  const flyTimer = React.useRef<number | null>(null);
  const keepFly = () => {
    if (flyTimer.current !== null) window.clearTimeout(flyTimer.current);
    flyTimer.current = null;
  };
  // Switching from one open panel to another waits a beat, so a diagonal move
  // toward the panel across other items doesn't swap it; reaching the panel
  // cancels the switch.
  const showFly = (key: string | null, now = false) => {
    keepFly();
    if (
      key &&
      !window.matchMedia("(hover: hover) and (min-width: 769px)").matches
    )
      return;
    const apply = () => {
      const right = aside.current?.getBoundingClientRect().right;
      if (right != null) setFlyLeft(right);
      setFlyKey(key);
    };
    if (now || flyKey === null || flyKey === key) apply();
    else flyTimer.current = window.setTimeout(apply, 140);
  };
  const hideFly = () => {
    keepFly();
    flyTimer.current = window.setTimeout(() => setFlyKey(null), 250);
  };
  React.useEffect(() => keepFly, []);
  const [glossary, setGlossary] = React.useState(false);
  const [installing, setInstalling] = React.useState(false);
  // An org admin's first screen is the org, not their own usage.
  React.useEffect(() => {
    if (window.location.hash.split("?")[0] || !isOrgAdmin(org)) return;
    const { pathname, search, hash } = window.location;
    const q = hash.includes("?") ? `?${hash.split("?")[1]}` : "";
    window.history.replaceState(
      null,
      "",
      `${pathname}${search}#organization/home${q}`,
    );
    navigate("organization", "home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (!navOpen) return;
    const main = document.getElementById("main");
    const mobile = window.matchMedia("(max-width: 768px)");
    if (!mobile.matches) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (main) main.inert = true;
    aside.current?.querySelector<HTMLElement>(".nav-item")?.focus();
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setNavOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = [
        ...(aside.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex="0"]',
        ) ?? []),
      ].filter((el) => el.getClientRects().length > 0);
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    const onResize = () => {
      if (!mobile.matches) setNavOpen(false);
    };
    mobile.addEventListener("change", onResize);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("keydown", onEsc);
      mobile.removeEventListener("change", onResize);
      if (main) main.inert = false;
      document.body.style.overflow = previousOverflow;
      if (mobile.matches)
        document.querySelector<HTMLElement>(".sidebar-toggle")?.focus();
    };
  }, [navOpen]);
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "\\" || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      slide(() =>
        setNavModeState((cur) => {
          const next: NavMode = cur === "rail" ? "expanded" : "rail";
          writeNavMode(next);
          return next;
        }),
      );
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const topNav = [
    ...NAV,
    ...(org
      ? []
      : [
          ...(me.sub_credited
            ? []
            : [
                {
                  key: "billing" as Section,
                  label: "Subscription",
                  icon: "subscription" as NavIconName,
                },
              ]),
          ...(me.api_key_enabled || me.sub_credited
            ? [
                {
                  key: "credits" as Section,
                  label: "Credits",
                  icon: "credits" as NavIconName,
                },
              ]
            : []),
        ]),
  ];
  const tailNav = org
    ? [
        {
          key: "leaderboard" as Section,
          label: "Leaderboard",
          icon: "trophy" as NavIconName,
        },
      ]
    : [];
  const name = user?.fullName || me.email?.split("@")[0] || "Account";
  const email = me.email || user?.primaryEmailAddress?.emailAddress || "";

  const go = (s: Section, t?: OrgTab) => {
    navigate(s, t);
    setNavOpen(false);
  };

  // A page's views, listed in the panel that opens beside the sidebar on hover.
  const viewsOf = (
    key: string,
  ): {
    title: string;
    items: Layer<string>[];
    open: (v: string) => void;
  } | null => {
    if (key === "overview")
      return {
        title: "My usage",
        items: USAGE_LAYERS,
        open: (v) =>
          navigate("overview", undefined, v === "overview" ? undefined : v),
      };
    const t = key as OrgTab;
    const items: Layer<string>[] | null =
      t === "visibility"
        ? [...COST_VIEWS]
        : t === "activity"
          ? ACTIVITY_LAYERS
          : t === "assets"
            ? INVENTORY_LAYERS
            : t === "budgets"
              ? GOVERNANCE_LAYERS
              : t === "plans"
                ? peopleLayers(canManageDevices(org))
                : t === "settings"
                  ? ORG_SETTINGS_LAYERS
                  : null;
    if (!items) return null;
    return {
      title: orgTabs(org).find((x) => x.key === t)?.label ?? "",
      items,
      open: (v) => {
        if (!TAB_PARAM.has(t)) return navigate("organization", t, v);
        // These pages read their view from ?tab=, first view by default, and
        // follow it on hashchange, which replaceState alone doesn't fire.
        navigate("organization", t);
        setHashParam("tab", v === items[0].value ? null : v);
        window.dispatchEvent(new HashChangeEvent("hashchange"));
      },
    };
  };
  const fly = flyKey ? viewsOf(flyKey) : null;
  const flyCurrent =
    flyKey === "overview"
      ? section === "overview"
        ? (sub ?? "overview")
        : ""
      : section === "organization" && orgTab === flyKey
        ? ((TAB_PARAM.has(orgTab) ? getHashParam("tab") : sub?.split(".")[0]) ??
          fly?.items[0]?.value ??
          "")
        : "";
  // Hover and focus open the panel; leaving the item and the panel closes it.
  const hover = (key: string) => ({
    "data-fly": viewsOf(key) ? "" : undefined,
    "aria-haspopup": viewsOf(key) ? ("menu" as const) : undefined,
    "aria-expanded": viewsOf(key) ? flyKey === key : undefined,
    onMouseEnter: () => showFly(viewsOf(key) ? key : null),
    onMouseLeave: hideFly,
    onFocus: () => showFly(viewsOf(key) ? key : null, true),
    onBlur: (e: React.FocusEvent) => {
      if (!flyRef.current?.contains(e.relatedTarget as Node | null)) hideFly();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" && viewsOf(key)) {
        e.preventDefault();
        showFly(key, true);
        requestAnimationFrame(() =>
          flyRef.current?.querySelector<HTMLElement>("button")?.focus(),
        );
      }
    },
  });

  return (
    <ToastProvider>
      <div className="app-shell app-shell-row">
        <button
          type="button"
          className="skip-link"
          onClick={() => document.getElementById("main")?.focus()}
        >
          Skip to content
        </button>
        <nav
          ref={aside}
          id="primary-nav"
          aria-label="Main"
          className={`sidebar ${navOpen ? "open" : ""} ${navMode === "rail" ? "rail" : ""}`}
        >
          <div className="brand">
            <Brand />
            <button
              type="button"
              className="nav-mode-btn"
              title={
                navMode === "rail"
                  ? "Expand sidebar (⌘\\)"
                  : "Collapse to icons (⌘\\)"
              }
              aria-label={
                navMode === "rail" ? "Expand sidebar" : "Collapse to icons"
              }
              aria-expanded={navMode !== "rail"}
              onClick={() =>
                setNavMode(navMode === "rail" ? "expanded" : "rail")
              }
            >
              <NavIcon name="panel" />
            </button>
            <button
              type="button"
              className="sidebar-close"
              aria-label="Close navigation"
              onClick={() => setNavOpen(false)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 3l8 8M11 3l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <WorkspaceSwitcher refresh={refresh} />
          <div className="nav-section">Personal</div>
          {topNav.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-label={s.label}
              {...hover(s.key)}
              className={`nav-item ${section === s.key && section !== "organization" ? "active" : ""}`}
              aria-current={
                section === s.key && section !== "organization"
                  ? "page"
                  : undefined
              }
              onClick={() => go(s.key)}
            >
              <span className="nav-glyph">
                <NavIcon name={s.icon} />
              </span>
              <span>{s.label}</span>
            </button>
          ))}
          {isOrgAdmin(org) && (
            <>
              <div className="nav-section">Organization</div>
              {orgTabs(org).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  aria-label={t.label}
                  {...hover(t.key)}
                  className={`nav-item ${
                    section === "organization" && orgTab === t.key
                      ? "active"
                      : ""
                  }`}
                  aria-current={
                    section === "organization" && orgTab === t.key
                      ? "page"
                      : undefined
                  }
                  onClick={() => go("organization", t.key)}
                >
                  <span className="nav-glyph">
                    <NavIcon name={ORG_ICON[t.key]} />
                  </span>
                  <span>{t.label}</span>
                </button>
              ))}
            </>
          )}
          <div className="nav-section">More</div>
          <button
            type="button"
            aria-label="Install CLI"
            aria-haspopup="dialog"
            className="nav-item"
            onClick={() => {
              setInstalling(true);
              setNavOpen(false);
            }}
          >
            <span className="nav-glyph">
              <NavIcon name="terminal" />
            </span>
            <span>Install CLI</span>
          </button>
          {tailNav.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-label={s.label}
              className={`nav-item ${section === s.key ? "active" : ""}`}
              aria-current={section === s.key ? "page" : undefined}
              onClick={() => go(s.key)}
            >
              <span className="nav-glyph">
                <NavIcon name={s.icon} />
              </span>
              <span>{s.label}</span>
            </button>
          ))}
          <div className="nav-group">
            <button
              type="button"
              className="nav-item nav-parent"
              aria-label="Resources"
              aria-expanded={resourcesOpen}
              onClick={() => {
                if (navMode === "rail") setNavMode("expanded");
                setResourcesOpen((v) => !v);
              }}
            >
              <span className="nav-glyph">
                <NavIcon name="resources" />
              </span>
              <span>Resources</span>
              <Icon
                name="chevron-right"
                size={14}
                className={`nav-caret ${resourcesOpen ? "open" : ""}`}
              />
            </button>
            {resourcesOpen && (
              <div className="nav-sub">
                <button
                  type="button"
                  className="nav-subitem"
                  onClick={() => {
                    setGlossary(true);
                    setNavOpen(false);
                  }}
                >
                  How the numbers work
                </button>
                {RESOURCES.map((r) => (
                  <a
                    key={r.key}
                    className="nav-subitem is-external"
                    href={`${landingUrl()}${r.path}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setNavOpen(false)}
                  >
                    {r.label}
                    <span className="nav-ext" aria-hidden="true">
                      ↗
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="user-chip-wrap">
            <button
              type="button"
              className={`user-chip ${menuOpen ? "active" : ""}`}
              title={menuOpen ? undefined : name}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="user-chip-avatar">
                {initialsOf(name, me.email || "")}
              </span>
              <span className="user-chip-text">
                <span className="user-chip-name">{name}</span>
                <span className="user-chip-sub" title={email}>
                  {email}
                </span>
              </span>
              <span className={`user-chip-chev ${menuOpen ? "open" : ""}`}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 15l6-6 6 6" />
                </svg>
              </span>
            </button>
            <UserMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              onNavigate={go}
              onTour={
                org && isOrgAdmin(org) && !org.sub_credited
                  ? () => window.dispatchEvent(new Event(TOUR_EVENT))
                  : undefined
              }
            />
          </div>
        </nav>
        {fly && (
          <div
            ref={flyRef}
            className="nav-fly"
            style={{ left: flyLeft }}
            onMouseEnter={keepFly}
            onMouseLeave={hideFly}
            onFocus={keepFly}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                hideFly();
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "ArrowLeft") {
                e.preventDefault();
                setFlyKey(null);
                aside.current
                  ?.querySelector<HTMLElement>(`[aria-label="${fly.title}"]`)
                  ?.focus();
              }
            }}
          >
            <div className="nav-fly-title">{fly.title}</div>
            <LayersNav
              menu
              title={fly.title}
              value={flyCurrent}
              options={fly.items}
              onChange={(v) => {
                keepFly();
                setFlyKey(null);
                fly.open(v);
              }}
            />
          </div>
        )}
        {glossary && <Glossary onClose={() => setGlossary(false)} />}
        {installing && <InstallDialog onClose={() => setInstalling(false)} />}
        {navOpen && (
          <div className="sidebar-scrim" onClick={() => setNavOpen(false)} />
        )}
        <main className="sidebar-main" id="main" tabIndex={-1}>
          <div className="mobile-topbar">
            <button
              type="button"
              className="sidebar-toggle"
              aria-label={navOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={navOpen}
              aria-controls="primary-nav"
              onClick={() => setNavOpen((v) => !v)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M2.5 4.5h11M2.5 8h11M2.5 11.5h7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <Brand />
          </div>
          <div className="content-pad">
            <RangeProvider>
              <PageBoundary resetKey={`${section}/${orgTab}`}>
                <SectionRouter
                  section={section}
                  me={me}
                  org={org}
                  authHeaders={authHeaders}
                  refresh={refresh}
                  orgTab={orgTab}
                  orgSub={sub}
                  onOrgTab={(t, s) =>
                    section === "organization"
                      ? navigate("organization", t, s)
                      : navigate(section, undefined, s)
                  }
                />
              </PageBoundary>
            </RangeProvider>
          </div>
        </main>
      </div>
    </ToastProvider>
  );
}
